<script>
  import { onDestroy } from 'svelte';
  import { geoNaturalEarth1, geoPath } from 'd3-geo';
  import { scaleSqrt } from 'd3-scale';

  import Bubbles from './Bubbles.svelte';
  import Arcs from './Arcs.svelte';
  import Labels from './Labels.svelte';

  import { isoLookup, supportsTouch, opacityScale } from '../utils';
  import {
    bubbleMapData,
    activeCountry,
    weightExtent,
    activeCategory,
    weightSum,
    activeCategoryItem,
    mapTransform
  } from '../stores';
  import {
    categories,
    bubbleMinRadius,
    bubbleMaxRadius,
    targetIsoCode
  } from '../config';

  export let width;
  export let height;
  export let geojson;
  export let tooltipIsoCode;
  export let tooltipPosition;

  // base map countries
  let projection;
  let pathGenerator;
  let countries;

  $: {
    projection = geoNaturalEarth1().fitSize([width, height], geojson);
    pathGenerator = geoPath(projection);
  }

  $: countries = geojson.features.map(feat => ({
    ...feat,
    properties: {
      ...feat.properties,
      data: $bubbleMapData[feat.properties.id]
    }
  }));

  $: isActiveCountry = feat =>
    !$activeCountry ||
    ($activeCountry && feat.properties.id === $activeCountry);

  $: activeCountries = countries.filter(isActiveCountry);

  $: inactiveCountries = countries.filter(feat => !isActiveCountry(feat));

  // countries event handler
  const onFeatureMove = evt => {
    tooltipPosition = {
      x: evt.clientX,
      y: evt.clientY
    };
  };

  const onFeatureEnter = (evt, properties) => {
    if (supportsTouch) {
      return null;
    }

    onFeatureMove(evt);

    tooltipIsoCode = properties.id;
  };

  const onFeatureLeave = evt => {
    tooltipIsoCode = null;
  };

  const onFeatureClick = id =>
    activeCountry.set(id === $activeCountry ? null : id);

  // bubbles
  let minColor;
  let maxColor;
  $: {
    const cat = categories.find(cat => cat.key === $activeCategory);

    minColor = cat.minColor;
    maxColor = cat.color;
  }

  $: bubbleScale = scaleSqrt()
    .domain($weightExtent)
    .range([bubbleMinRadius, bubbleMaxRadius]);

  $: labels = countries
    .filter(feat => feat.properties.data && feat.properties.id)
    .sort((a, b) => b.properties.data.weight - a.properties.data.weight)
    .map(feat => {
      const weight = $bubbleMapData[feat.properties.id].weight;
      const [cx, cy] = pathGenerator.centroid(feat);
      return {
        cx,
        cy,
        id: feat.properties.id,
        offset: weight === 0 ? 0 : bubbleScale(weight) + 15
      };
    })
    .slice(0, 5);

  $: bubbles = countries
    .filter(c => c.properties.data)
    .map(feat => {
      const id = feat.properties.id;
      const [cx, cy] = pathGenerator.centroid(feat);
      const weight = $bubbleMapData[id].weight;
      const isActive =
        !$activeCountry || ($activeCountry && id === $activeCountry);

      const proportion = $bubbleMapData[id].weight / $weightSum;
      const isHovered = tooltipIsoCode === id;

      return {
        id,
        isVisible: weight > 0,
        cx,
        cy,
        opacity: opacityScale(proportion),
        r: weight === 0 ? 0 : bubbleScale(weight),
        fill: isActive || isHovered ? maxColor : '#DEDEDE',
        stroke: isActive || isHovered ? maxColor : '#D2D2D2',
        isHovered: isHovered
      };
    });
</script>

<style>
  .km-country {
    fill: rgb(248, 248, 248);
    stroke: rgb(225, 225, 225);
  }

  .km-country.selected {
    stroke-width: 1.5;
    stroke-opacity: 0.4;
  }

  .km-has-data {
    cursor: pointer;
  }

  .km-has-data:hover {
    stroke: rgb(200, 200, 200);
  }
</style>

<g transform={$mapTransform}>
  {#each inactiveCountries as feat}
    {#if !feat.properties.data || !feat.properties.data.weight}
      <path
        d={pathGenerator(feat)}
        class="km-country"
        class:inactive={$activeCountry && feat.properties.id !== $activeCountry} />
    {:else}
      <path
        d={pathGenerator(feat)}
        on:mouseenter={evt => onFeatureEnter(evt, feat.properties)}
        on:mousemove={onFeatureMove}
        on:mouseleave={onFeatureLeave}
        on:click={() => onFeatureClick(feat.properties.id)}
        class="km-country km-has-data"
        class:selected={feat.properties.id === $activeCountry}
        style={`stroke: ${feat.properties.id === $activeCountry ? maxColor : null};`} />
    {/if}
  {/each}
  {#each activeCountries as feat}
    {#if !feat.properties.data || !feat.properties.data.weight}
      <path
        d={pathGenerator(feat)}
        class="km-country"
        class:inactive={$activeCountry && feat.properties.id !== $activeCountry} />
    {:else}
      <path
        d={pathGenerator(feat)}
        on:mouseenter={evt => onFeatureEnter(evt, feat.properties)}
        on:mousemove={onFeatureMove}
        on:mouseleave={onFeatureLeave}
        on:click={() => onFeatureClick(feat.properties.id)}
        class="km-country km-has-data"
        class:selected={feat.properties.id === $activeCountry}
        style={`stroke: ${feat.properties.id === $activeCountry ? maxColor : null};`} />
    {/if}
  {/each}
</g>

<Bubbles data={bubbles} />
<Arcs data={bubbles} {projection} {tooltipIsoCode} />
<Labels {labels} />
